import type { TrackingDebugService } from './TrackingDebugService';

/** Point-in-time counters copied into each collect payload; {@link MeasurementStore.snapshot} resets most fields. */
export interface MeasurementSnapshot {
  variant_clicks: number;
  scroll_depth_max: number;
  scroll_depth_current: number;
  viewport_height: number;
  image: {
    viewed: number;
    clicked: number;
    zoomed: number;
  };
  reviews_reached: boolean;
  recommendations_reached: boolean;
}

/**
 * Mutable engagement counters for the current page view. **`snapshot()`** returns the delta since the last
 * snapshot and then clears cumulative counters — paired with the heartbeat interval so each payload carries
 * “what happened since last send” rather than lifetime totals.
 */
export class MeasurementStore {
  private variantClicks = 0;
  private scrollDepthMax = 0;
  private scrollDepthCurrent = 0;
  private viewportHeight = 0;

  private imageViewed = 0;
  private imageClicked = 0;
  private imageZoomed = 0;

  private reviewsReached = false;
  private recommendationsReached = false;

  constructor(private readonly debug?: TrackingDebugService) {}

  setViewportHeight(px: number): void {
    this.viewportHeight = Math.max(0, Math.floor(px));
  }

  setScrollDepthCurrent(px: number): void {
    const v = Math.max(0, Math.floor(px));
    this.scrollDepthCurrent = v;
    if (v > this.scrollDepthMax) {
      this.scrollDepthMax = v;
    }
  }

  incrementVariantClick(): void {
    this.variantClicks += 1;
  }

  incrementImageViewed(): void {
    this.imageViewed += 1;
  }

  incrementImageClicked(): void {
    this.imageClicked += 1;
  }

  incrementImageZoomed(): void {
    this.imageZoomed += 1;
  }

  setReviewsReached(): void {
    this.reviewsReached = true;
  }

  setRecommendationsReached(): void {
    this.recommendationsReached = true;
  }

  snapshot(): MeasurementSnapshot {
    const out: MeasurementSnapshot = {
      variant_clicks: this.variantClicks,
      scroll_depth_max: this.scrollDepthMax,
      scroll_depth_current: this.scrollDepthCurrent,
      viewport_height: this.viewportHeight,
      image: {
        viewed: this.imageViewed,
        clicked: this.imageClicked,
        zoomed: this.imageZoomed,
      },
      reviews_reached: this.reviewsReached,
      recommendations_reached: this.recommendationsReached,
    };

    this.debug?.log('MeasurementStore', 'snapshot (interval flush)', { ...out });

    // Reset interval counters for the next heartbeat window.
    this.variantClicks = 0;
    this.scrollDepthMax = 0;
    this.scrollDepthCurrent = 0;
    /* Viewport is not zeroed: keep last measured height until the next scroll handler updates it. */
    this.imageViewed = 0;
    this.imageClicked = 0;
    this.imageZoomed = 0;
    this.reviewsReached = false;
    this.recommendationsReached = false;

    return out;
  }
}
